import Popover from '@material-ui/core/Popover'
import { makeStyles } from '@material-ui/core/styles'
import CreateButton from 'core/components/buttons/CreateButton'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import Button from 'core/elements/button/Button'
import Theme from 'core/themes/model'
import React, { useCallback, useState } from 'react'

export interface PopoverButtonItem {
  title: string
  onClick: () => void
}

interface Props {
  items: PopoverButtonItem[]
}

/**
 * @summary This component is used to render deploy button with more options on click
 * @param items - Array of objects with title and onClick properties
 * @returns {React.ReactNode} The PopoverButton component
 */
const PopoverButton = ({ items }: Props) => {
  const classes = useStyles()
  const [openSettingPopover, setOpenSettingPopover] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const popoverRef = React.useRef(null)

  const handleClose = useCallback(() => {
    setOpenSettingPopover(false)
  }, [])

  const handleClick = useCallback((event) => {
    setOpenSettingPopover(true)
    setAnchorEl(event.currentTarget)
  }, [])

  const handleItemClick = useCallback(
    (cta) => {
      handleClose()
      cta()
    },
    [handleClose],
  )

  return (
    <>
      <CreateButton
        ref={popoverRef}
        onClick={handleClick}
        rightIcon="chevron-down"
        data-testid="create-cluster-button"
      >
        Deploy New Cluster
      </CreateButton>
      <Popover
        open={openSettingPopover}
        onClose={handleClose}
        anchorEl={anchorEl}
        ref={popoverRef}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: -8,
          horizontal: 'right',
        }}
        data-testid="create-cluster-popover"
      >
        <div className={classes.buttonPopover}>
          {items.map((item, index) => (
            <div className={classes.buttonPopoverItem} key={`${index}-${item.title}`}>
              <div className={classes.buttonPlusIcon}>
                <FontAwesomeIcon name="plus" light solid />
              </div>
              <Button
                fullWidth
                variant="secondary"
                onClick={() => handleItemClick(item?.onClick)}
                data-testid={`create-cluster-${item.title}`}
              >
                {item?.title}
              </Button>
            </div>
          ))}
        </div>
      </Popover>
    </>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  buttonPopover: {
    display: 'grid',
    gridAutoFlow: 'row',
    gridGap: theme.spacing(2),
    padding: theme.spacing(2),
  },
  buttonPopoverItem: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    gridGap: '0px 0px',

    '& button': {
      width: 'calc(100% - 36px)',
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
    },
  },
  buttonPlusIcon: {
    width: 36,
    height: 36,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.components.button.primary.background,
    borderTopLeftRadius: theme.shape.borderRadius,
    borderBottomLeftRadius: theme.shape.borderRadius,

    '& i': {
      color: theme.components.button.primary.color,
      fontWeight: 'bold',
    },
  },
}))

export default PopoverButton
